import React, { useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography,
  Table, TableHead, TableRow, TableCell, TableBody, Box, Stack, Divider, CircularProgress,
} from "@mui/material";
import dayjs from "dayjs";
import StatusChip from "./StatusChip";
import { OrderVM, OrderStatus } from "../data/types";
import { formatCAD } from "../utils/currency";

/** Allowed next statuses — mirrors the backend transition map (QA #2). */
const NEXT: Record<OrderStatus, OrderStatus[]> = {
  New: ["Confirmed", "Cancelled"],
  Pending: ["Confirmed", "Cancelled"],
  Confirmed: ["Preparing", "Cancelled"],
  Preparing: ["Delivered"],
  Delivered: [],
  Cancelled: [],
};

const ACTION_LABEL: Record<OrderStatus, string> = {
  Confirmed: "Confirm order",
  Preparing: "Start preparing",
  Delivered: "Mark delivered",
  Cancelled: "Cancel order",
  New: "New",
  Pending: "Pending",
};

const fmt = (d?: string) => (d ? dayjs(d).format("MMM D, YYYY") : "—");

interface Props {
  open: boolean;
  order: OrderVM | null;
  onClose: () => void;
  /** Distributor gets status-change controls; buyers see a read-only view. */
  canManage?: boolean;
  onStatusChange?: (next: OrderStatus) => Promise<void> | void;
}

const OrderDetailsDialog: React.FC<Props> = ({ open, order, onClose, canManage, onStatusChange }) => {
  const [busy, setBusy] = useState<OrderStatus | null>(null);
  if (!order) return null;

  const nexts = NEXT[order.status] ?? [];

  const act = async (next: OrderStatus) => {
    if (!onStatusChange) return;
    setBusy(next);
    try { await onStatusChange(next); } finally { setBusy(null); }
  };

  const subtotal = order.subtotal ?? (order.lines?.reduce((s, l) => s + l.lineTotal, 0) ?? 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
          <span>Order {order.orderId}</span>
          <StatusChip status={order.status} />
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={0.5} sx={{ mb: 2 }}>
          <Typography variant="body2"><b>Customer:</b> {order.customer || "—"}</Typography>
          <Typography variant="body2"><b>Order date:</b> {fmt(order.orderDate)}</Typography>
          <Typography variant="body2"><b>Requested delivery:</b> {fmt(order.deliveryDate)}</Typography>
        </Stack>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>Items ({order.items})</Typography>
        {order.lines && order.lines.length > 0 ? (
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell>SKU</TableCell>
                  <TableCell>Pack</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell align="right">Unit</TableCell>
                  <TableCell align="right">Line total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {order.lines.map((l, i) => (
                  <TableRow key={i}>
                    <TableCell>{l.productName}</TableCell>
                    <TableCell>{l.sku || "—"}</TableCell>
                    <TableCell>{l.packagingUnit || "—"}</TableCell>
                    <TableCell align="right">{l.quantity}</TableCell>
                    <TableCell align="right">{formatCAD(l.unitPrice)}</TableCell>
                    <TableCell align="right">{formatCAD(l.lineTotal)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">No line items recorded for this order.</Typography>
        )}

        <Divider sx={{ my: 1.5 }} />
        <Stack spacing={0.5} sx={{ ml: "auto", maxWidth: 240 }}>
          <Row label="Subtotal" value={formatCAD(subtotal)} />
          {order.tax != null && <Row label="Estimated HST" value={formatCAD(order.tax)} />}
          <Row label="Total (CAD)" value={formatCAD(order.total)} bold />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ flexWrap: "wrap", gap: 1 }}>
        {canManage && nexts.map((n) => (
          <Button
            key={n}
            variant={n === "Cancelled" ? "outlined" : "contained"}
            color={n === "Cancelled" ? "error" : "primary"}
            disabled={busy !== null}
            onClick={() => act(n)}
          >
            {busy === n ? <CircularProgress size={18} color="inherit" /> : ACTION_LABEL[n]}
          </Button>
        ))}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const Row: React.FC<{ label: string; value: string; bold?: boolean }> = ({ label, value, bold }) => (
  <Stack direction="row" justifyContent="space-between">
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: bold ? 700 : 400 }}>{value}</Typography>
  </Stack>
);

export default OrderDetailsDialog;
